import { LitElement, css, html } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import '@maersk-global/mds-components-core';
import '@maersk-global/mds-components-core/mc-popover';
import { ActivityLogItem, createActivityLogItems, UnifiedNotification } from './activity-schema';
import { ImminentRiskModel } from '../../../src/models/imminent-risk-model';
import { ImminentRiskEvents } from '../../../src/models/imminent-risk-model-impl';
import { FeatureFlagsEvents } from '../../../src/models/feature-flag-model';
import { VesselNotificationsModel } from '../../../src/models/vessel-notifications-model';
import { VesselNotificationsEventNames } from '../../../src/models/types/vessel-notifications.types';
import { SvgIcons } from '../../voyage-svg-icons/src/voyage-svg-icons';

@customElement('activity-log')
export class ActivityLog extends LitElement {
    @state() notifications: UnifiedNotification[] = [];
    @state() searchQuery: string = '';
    @state() private activityLogItems: ActivityLogItem[] = [];
    @state() private isLoading = true;
    private imminentNotification: UnifiedNotification | null = null;
    private handleFeatureFlagsInitialized = () => this._fetchNotifications();
    private handleWeatherNotificationsUpdate = () => this.updateActivityLogItems();

    static styles = css`
        .notifications {
            height: 75vh;
            overflow-y: auto;
        }
        .empty-state {
            padding: 16px;
            text-align: center;
            color: #767676;
        }
        .search-container {
            padding: 16px;
        }
        .popover-trigger {
            display: flex;
            align-items: center;
            cursor: pointer;
            gap: 5px;
            font-family: MaerskText;
            font-size: 14px;
            line-height: 16px;
            margin-right: 8px;
        }
        .popover-content > *:not(:last-child) {
            border-bottom: 1px solid var(--neutrals-grey-06);
        }
        .popover-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            gap: 8px;
            padding: 20px 16px;
            font-size: 20px;
            font-family: MaerskText;
        }
        .activity-log-icon {
            position: relative;
        }
        .red-dot {
            width: 12px;
            height: 12px;
            background-color: #c83819;
            border-radius: 50%;
            position: absolute;
            top: -1px;
            left: -1px;
        }
        .notification-card {
        background: #e6f1fb;
        padding: 14px 16px 12px 16px;
        box-shadow: 0px 1px 3px rgba(0,0,0,0.05);
        font-family: "Maersk Text", sans-serif;
        color: #001b44;
        }

        .notification-header {
        display: flex;
        align-items: flex-start;
        gap: 8px;
        font-size: 14px;
        line-height: 1.4;
        margin-bottom: 1rem;
        }

        .notification-header .icon {
        flex-shrink: 0;
        margin-top: 2px;
        }
        .risk-icon {
        width: 26px;
        height: 26px;
        border-radius: 50%;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        }

        .notification-body {
        display: flex;
        justify-content: flex-start;
        margin-top: 4px;
        margin-left: 1.5rem
        }

        .notification-footer {
        text-align: right;
        color: #6b7280;
        font-size: 12px;
        margin-top: 6px;
        }
    `;
    
    connectedCallback() {
        super.connectedCallback();
        window.addEventListener(FeatureFlagsEvents.featureBasedFlagsInitialized, this.handleFeatureFlagsInitialized);
        VesselNotificationsModel?.addEventListener(
            VesselNotificationsEventNames.NotificationsUpdated,
            this.handleWeatherNotificationsUpdate,
        );
        this._fetchNotifications();
    }

    disconnectedCallback() {
        window.removeEventListener(FeatureFlagsEvents.featureBasedFlagsInitialized, this.handleFeatureFlagsInitialized);
        VesselNotificationsModel?.removeEventListener(
            VesselNotificationsEventNames.NotificationsUpdated,
            this.handleWeatherNotificationsUpdate,
        );
        super.disconnectedCallback();
    }

    private updateActivityLogItems() {
        const weatherNotifications = VesselNotificationsModel?.notifications || [];
        this.notifications = this.imminentNotification
            ? [this.imminentNotification, ...weatherNotifications]
            : [...weatherNotifications];
        this.activityLogItems = createActivityLogItems(this.notifications);
    }

    private async _fetchNotifications() {
        try {
            this.isLoading = true;
            const [imminentResult] = await Promise.allSettled([
                ImminentRiskModel?.getCurrentImminentRiskAlert() || Promise.resolve(null),
                VesselNotificationsModel?.loadWeatherAlarms() || Promise.resolve(),
            ]);
            if (imminentResult.status === 'fulfilled') {
                this.imminentNotification = imminentResult.value;
            }
            this.updateActivityLogItems();
        } catch (err) {
            console.error('Error fetching notifications:', err);
        } finally {
            this.isLoading = false;
            this.requestUpdate();
        }
    }

    private renderIcon(item: ActivityLogItem) {
        const icon = item.icon;
        if (!icon || typeof icon !== 'object' || !('bgColor' in icon)) return icon;
        const customIcon = icon.customName
            ? SvgIcons[icon.customName as keyof typeof SvgIcons]
            : null;
        return html`<span class="risk-icon" style=${`background-color:${icon.bgColor};color:${icon.color || '#fff'}`}>
            ${customIcon || html`<mc-icon icon=${icon.name} color=${icon.color || '#fff'} size="16"></mc-icon>`}
        </span>`;
    }

    private handleSearchInput(e: Event) {
        const input = e.target as HTMLInputElement;
        this.searchQuery = input.value;
    }

    private filteredItems(query: string) {
        if (!query.trim()) return this.activityLogItems;
        const normalized = query.toLowerCase().trim();
        return this.activityLogItems.filter((n) =>
            [n.title, n.description].some((t) => t?.toLowerCase().includes(normalized))
        );
    }

    render() {
        const filteredItems = this.filteredItems(this.searchQuery);

        return html`
            <mc-popover width="354px" position="bottom-center" @show=${() => this._fetchNotifications()}>
                <span slot="trigger" class="popover-trigger">
                    <div class="activity-log-icon">
                        <mc-icon icon="file"></mc-icon>
                        ${this.activityLogItems.length ? html`<span class="red-dot"></span>` : null}
                    </div>
                    Activity log
                </span>

                <div class="popover-content">
                    <div class="popover-header">
                        <span>Activity log</span>
                        <mc-switch label="Needs action"></mc-switch>
                    </div>

                    <div class="search-container">
                        <mc-input
                            placeholder="Search notifications"
                            icon="magnifying-glass"
                            hiddenlabel
                            .value=${this.searchQuery}
                            @input=${this.handleSearchInput}
                        ></mc-input>
                    </div>

                    <div class="notifications">
                        ${this.isLoading
                            ? html`<div class="empty-state">Loading...</div>`
                            : filteredItems.length > 0
                            ? filteredItems.map(
                                  (n) =>
                                      html`<div class="notification-card">
                                <div class="notification-header">
                                <span class="icon">${this.renderIcon(n)}</span>
                                <span class="title">${n.title}${n.description ? html`<div>${n.description}</div>` : null}</span>
                                </div>

                                ${n.actions?.length ? html`<div class="notification-body">
                                <mc-button
                                    appearance="primary"
                                    size="medium"
                                    class="action-btn"
                                    @click=${() =>
                                    ImminentRiskModel?.dispatchEvent(
                                        new CustomEvent(ImminentRiskEvents.hasImminentRisk, {
                                            detail: {log_id : localStorage.getItem('imminentRiskLogId')},
                                        }),
                                    )}
                                >
                                    ${n.actions?.[0].label}
                                </mc-button>
                                </div>` : null}

                                <div class="notification-footer">${n.timestamp}</div>
                            </div>`
                              )
                            : html`<div class="empty-state">
                                  ${this.searchQuery
                                      ? 'No notifications match your search'
                                      : 'No notifications yet'}
                              </div>`}
                    </div>
                </div>
            </mc-popover>
        `;
    }
}
